// Cached Data Service - fetches from background service cache
// This is fast and doesn't make external API calls

class CachedDataService {
  constructor() {
    // Cache for preventing rapid re-fetches
    this.cache = {
      prices: null,
      news: null,
      pricesExpiry: null,
      newsExpiry: null
    };

    // Cache frontend data for 30 seconds to prevent rapid re-fetches
    this.frontendCacheDuration = 30000;
  }

  // Check if frontend cache is valid
  isFrontendCacheValid(type) {
    const expiry = this.cache[`${type}Expiry`];
    return expiry && Date.now() < expiry;
  }

  // Set frontend cache
  setFrontendCache(type, data) {
    this.cache[type] = data;
    this.cache[`${type}Expiry`] = Date.now() + this.frontendCacheDuration;
  }

  // Fetch stock prices from background service cache
  async fetchStockPrices() {
    // Check frontend cache first
    if (this.isFrontendCacheValid('prices')) {
      console.log('📦 Using frontend cache for prices');
      return {
        success: true,
        data: this.cache.prices,
        cached: true
      };
    }

    try {
      console.log('📡 Fetching prices from background cache...');
      
      // Try multiple potential URLs for the cached data (prioritize working paths)
      const urls = [
        `/cached-prices.json?t=${Date.now()}`,
        `/api/cached-prices.json?t=${Date.now()}`,
        `./api/cached-prices.json?t=${Date.now()}`
      ];

      let result = null;
      let lastError = null;

      for (const url of urls) {
        try {
          console.log(`🔍 Trying URL: ${url}`);
          const response = await fetch(url, {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });

          if (response.ok) {
            result = await response.json();
            console.log(`✅ Successfully fetched from: ${url}`);
            break;
          } else {
            console.log(`❌ Failed to fetch from ${url}: HTTP ${response.status}`);
          }
        } catch (error) {
          console.log(`❌ Error fetching from ${url}: ${error.message}`);
          lastError = error;
        }
      }

      if (result && result.prices) {
        // Cache the result in frontend
        this.setFrontendCache('prices', result.prices);
        
        console.log(`✅ Loaded ${Object.keys(result.prices).length} cached prices from background service`);
        console.log(`📅 Background data last updated: ${result.lastUpdated}`);
        
        return {
          success: true,
          data: result.prices,
          lastUpdated: result.lastUpdated,
          source: 'background-cache'
        };
      } else {
        throw lastError || new Error('No cached price data found at any URL');
      }
    } catch (error) {
      console.error('❌ Failed to fetch cached prices:', error.message);
      console.log('🔄 Falling back to direct API service...');
      
      // Fallback to the original real data service as backup
      try {
        const realDataService = await import('./realDataService');
        const trackedTickers = ['MSTR', 'MARA', 'RIOT', 'COIN', 'TSLA', 'HUT', 'CLSK'];
        const fallbackResult = await realDataService.default.fetchRealStockPrices(trackedTickers);
        
        if (fallbackResult.success) {
          console.log('✅ Fallback API service provided data');
          return fallbackResult;
        }
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError.message);
      }
      
      // Return empty data rather than failing completely
      return {
        success: false,
        data: {},
        error: `Cache and fallback unavailable: ${error.message}`
      };
    }
  }

  // Fetch news from background service cache
  async fetchNews() {
    // Check frontend cache first
    if (this.isFrontendCacheValid('news')) {
      console.log('📦 Using frontend cache for news');
      return {
        success: true,
        data: this.cache.news,
        cached: true
      };
    }

    try {
      console.log('📡 Fetching news from background cache...');
      
      // Try multiple potential URLs for the cached data (prioritize working paths)
      const urls = [
        `/cached-news.json?t=${Date.now()}`,
        `/api/cached-news.json?t=${Date.now()}`,
        `./api/cached-news.json?t=${Date.now()}`
      ];

      let result = null;
      let lastError = null;

      for (const url of urls) {
        try {
          console.log(`🔍 Trying news URL: ${url}`);
          const response = await fetch(url, {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });

          if (response.ok) {
            result = await response.json();
            console.log(`✅ Successfully fetched news from: ${url}`);
            break;
          } else {
            console.log(`❌ Failed to fetch news from ${url}: HTTP ${response.status}`);
          }
        } catch (error) {
          console.log(`❌ Error fetching news from ${url}: ${error.message}`);
          lastError = error;
        }
      }

      if (result && Array.isArray(result.news)) {
        // Cache the result in frontend
        this.setFrontendCache('news', result.news);
        
        console.log(`✅ Loaded ${result.news.length} cached news articles from background service`);
        console.log(`📅 Background data last updated: ${result.lastUpdated}`);
        
        return {
          success: true,
          data: result.news,
          lastUpdated: result.lastUpdated,
          source: 'background-cache'
        };
      } else {
        throw lastError || new Error('No cached news data found at any URL');
      }
    } catch (error) {
      console.error('❌ Failed to fetch cached news:', error.message);
      console.log('🔄 Falling back to direct API service...');
      
      // Fallback to the original real data service as backup
      try {
        const realDataService = await import('./realDataService');
        const fallbackResult = await realDataService.default.fetchBitcoinTreasuryNews();
        
        if (fallbackResult.success) {
          console.log('✅ Fallback news API service provided data');
          return fallbackResult;
        }
      } catch (fallbackError) {
        console.error('❌ News fallback also failed:', fallbackError.message);
      }
      
      return {
        success: false,
        data: [],
        error: `Cache and fallback unavailable: ${error.message}`
      };
    }
  }

  // Fetch service status
  async fetchServiceStatus() {
    try {
      // Try working paths first
      const urls = [
        `/service-status.json?t=${Date.now()}`,
        `/api/service-status.json?t=${Date.now()}`
      ];

      for (const url of urls) {
        try {
          const response = await fetch(url, {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });

          if (response.ok) {
            const status = await response.json();
            return {
              success: true,
              data: status
            };
          }
        } catch (error) {
          console.log(`Failed to fetch status from ${url}: ${error.message}`);
        }
      }

      throw new Error('Status not available at any URL');
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get companies data (placeholder for compatibility)
  async detectNewTreasuryCompanies() {
    try {
      const response = await fetch(`/api/cached-companies.json?t=${Date.now()}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          data: result.companies || []
        };
      }
    } catch (error) {
      console.log('Companies data not available:', error.message);
    }

    // Return empty companies for now
    return {
      success: true,
      data: []
    };
  }

  // Clear frontend cache
  clearCache() {
    this.cache = {
      prices: null,
      news: null,
      pricesExpiry: null,
      newsExpiry: null
    };
    console.log('🗑️ Frontend cache cleared');
  }
}

export default new CachedDataService();